/**
 * Central route paths — single source of truth for navigation and redirects.
 * Public reading & discovery live outside /app; authenticated app shell under /app.
 */

/** Slugs that map to the public catalog route `/${slug}` (single genre-first list). */
export const PUBLIC_CATALOG_SLUGS = Object.freeze(["read"]);

export function isPublicCatalogSlug(value) {
  return PUBLIC_CATALOG_SLUGS.includes(String(value || "").toLowerCase());
}

/** Public browse path (legacy callers may pass old type slugs; all resolve to `/read`). */
export function browsePath(_slug) {
  return "/read";
}

export const PATHS = {
  HOME: "/",
  AUTH_LOGIN: "/auth/login",
  AUTH_REGISTER: "/auth/register",
  RESET_PASSWORD: "/reset-password",
  BECOME_WRITER: "/become-writer",

  /** Public legal & support (single source for in-app links) */
  CONTACT: "/contact",
  CANCELLATION_POLICY: "/cancellation-policy",
  TERMS: "/terms-conditions",
  PRIVACY: "/privacy-policy",

  /** Public writer subscription marketing (compare tiers before signing up) */
  WRITER_PRICING: "/writer-pricing",

  /** Authenticated app (dashboard, activity, library management) */
  APP: "/app",
  APP_DASHBOARD: "/app/dashboard",
  /** Reader dashboard revamp (split layout: 3 / 6 / 3) */
  APP_SPLIT_READER_DASHBOARD: "/app/reader-split",
  APP_EXPLORE: "/app/explore",
  APP_ACTIVITY: "/app/activity",
  APP_HISTORY: "/app/history",
  APP_FOLLOWING: "/app/following",
  APP_LIBRARY: "/app/library",
  APP_SUBSCRIPTIONS: "/app/subscriptions",
  APP_SETTINGS: "/app/settings",
  APP_CREATE: "/app/create",
  APP_CONTENT: "/app/content",
  APP_ONBOARDING: "/app/onboarding",
  APP_WRITER_SUBSCRIPTION: "/app/writer-subscription",

  /** Public catalog list: `/read` */
  browse: (slug) => browsePath(slug),

  /** Public browse hub */
  BROWSE: "/browse",

  /** Public landing nav — community & discovery */
  WRITERS: "/writers",
  READING_CHALLENGES: "/reading-challenges",
  /** Trending list (minimal layout; avoids full explore header) */
  TRENDING: "/trending",
  /** @deprecated Use PATHS.TRENDING */
  EXPLORE_TRENDING: "/trending",

  SEARCH: "/search",

  /** Content read URLs (public) */
  contentView: (id) => `/content/${id}/view`,
  poemView: (id) => `/content/${id}/view`,
  storyView: (id) => `/content/${id}/view`,
  bookView: (id) => `/content/${id}/view`,

  contentEdit: (id) => `/content/${id}/edit`,
  poemEdit: (id) => `/content/${id}/edit`,
  storyEdit: (id) => `/content/${id}/edit`,
  bookEdit: (id) => `/content/${id}/edit`,

  contentNew: () => "/content/new",
  poemNew: () => "/content/new",
  storyNew: () => "/content/new",
  bookNew: () => "/content/new",

  PROFILE: (userId) => `/profile/${userId}`,

  ADMIN_DASHBOARD: "/admin/dashboard",
  ADMIN_USERS: "/admin/users",
  ADMIN_CONTENT: "/admin/content",
  ADMIN_REPORTS: "/admin/reports",
  ADMIN_ACTIVITY: "/admin/activity",
  ADMIN_ENQUIRIES: "/admin/enquiries",
  ADMIN_SETTINGS: "/admin/settings",
  ADMIN_BULK_GENERATOR: "/admin/bulk-generator",
  ADMIN_MODERATION: "/admin/moderation",
  ADMIN_ROLES: "/admin/roles",
  ADMIN_CATEGORIES_TAGS: "/admin/categories-tags",
  ADMIN_ABUSE: "/admin/abuse",
  ADMIN_NOTIFICATIONS_MGMT: "/admin/notification-settings",
  ADMIN_CMS: "/admin/cms",
  ADMIN_FEATURE_FLAGS: "/admin/feature-flags",
  ADMIN_SEO: "/admin/seo",
  ADMIN_BACKUP: "/admin/backup",
  ADMIN_FEEDBACK: "/admin/feedback",
  /** Same enquiries inbox; alias for support ticket workflows */
  ADMIN_SUPPORT: "/admin/support",
};

/**
 * Only allow same-origin relative paths (blocks `//evil.com`, `https://…`, etc.).
 * @param {string} path
 */
export function isSafeInternalRedirectPath(path) {
  if (!path || typeof path !== "string") return false;
  const p = path.trim();
  if (!p.startsWith("/")) return false;
  if (p.startsWith("//")) return false;
  if (p.includes("://")) return false;
  return true;
}

/**
 * Login URL with post-auth redirect (must be a safe internal path)
 */
export function authLoginUrl(redirectPath) {
  if (!isSafeInternalRedirectPath(redirectPath)) {
    return PATHS.AUTH_LOGIN;
  }
  return `${PATHS.AUTH_LOGIN}?redirect=${encodeURIComponent(redirectPath.trim())}`;
}

export function authRegisterUrl(redirectPath) {
  const base = PATHS.AUTH_REGISTER;
  if (!isSafeInternalRedirectPath(redirectPath)) {
    return base;
  }
  return `${base}?redirect=${encodeURIComponent(redirectPath.trim())}`;
}
